import { useMemo, useState } from 'react';
import { useLocations, useRetryRun, useRunDetail, useRuns } from '../lib/hooks';
import { loadAuthState } from '../lib/auth-store';
import { getActiveMembership } from '../lib/role-access';
import { formatDateTimeEst } from '../lib/datetime';
import {
  Button,
  EmptyState,
  Icon,
  PageHeader,
  SkeletonList,
  StatusBadge,
  useToast,
} from '../components/ui';

/** Friendly names and explanations for each pipeline step. */
const STEP_LABELS: Record<string, { label: string; explain: string }> = {
  load_context: {
    label: 'Gather business details',
    explain: 'Loads the brief, services, and recent posts so the new post stays fresh.',
  },
  fetch_website_content: {
    label: 'Read the website',
    explain: 'Skims the business website for up-to-date details to mention.',
  },
  profitability_guardrail_check: {
    label: 'Check usage limits',
    explain: 'Makes sure this workspace is within its monthly usage allowance.',
  },
  generate_gbp_post: {
    label: 'Write the post',
    explain: 'Writes a post for this business and checks it against Google content policies.',
  },
  generate_media_asset: {
    label: 'Create the image',
    explain: 'Designs an on-brand image to attach to the post.',
  },
  publish_gbp_post: {
    label: 'Publish to Google',
    explain: 'Sends the finished post to the Google Business Profile.',
  },
  notify_internal_and_client: {
    label: 'Send notifications',
    explain: 'Emails your team (and the client, if set) a copy of the published post.',
  },
  update_schedule_fields: {
    label: 'Schedule the next post',
    explain: 'Books the next posting slot based on this location’s schedule.',
  },
  auto_reply_reviews: {
    label: 'Reply to new reviews',
    explain: 'Writes and posts replies to recent unanswered reviews, if enabled.',
  },
};

const TRIGGER_LABELS: Record<string, string> = {
  SCHEDULED: 'Scheduled',
  MANUAL: 'Started manually',
  API: 'Via API',
  RETRY: 'Retry',
};

/** Turns common backend errors into plain-English guidance. */
function explainError(message: string | null | undefined): string | null {
  if (!message) {
    return null;
  }

  if (/no healthy google account connection/i.test(message)) {
    return 'No Google account is connected for this workspace. Go to Google connection and connect the account that manages this profile.';
  }
  if (/missing gbpaccountid|missing gbplocationid|gbpAccountId or gbpLocationId/i.test(message)) {
    return 'This location is not linked to a Google Business Profile yet. Import it from your Google account, or fill in the Google IDs in the location settings.';
  }
  if (/quality gate failed/i.test(message)) {
    return 'The generated post did not pass our quality checks, so nothing was published. Try again — if it keeps happening, add more detail to the location’s content brief.';
  }
  if (/guardrail|limit/i.test(message) && /blocked|reached/i.test(message)) {
    return 'This workspace reached its monthly usage limit, so the post was paused. It will resume next cycle, or you can raise the limit in Billing.';
  }
  if (/fetch failed|enotfound|econnrefused|timeout/i.test(message)) {
    return 'We could not reach an external service (often the business website). This is usually temporary — try the run again.';
  }
  if (/401|403|invalid_grant|unauthorized/i.test(message)) {
    return 'Google rejected our access. Reconnect the Google account on the Google connection page and try again.';
  }

  return null;
}

export function RunsPage() {
  const auth = loadAuthState();
  const { toast } = useToast();
  const activeMembership = getActiveMembership(auth.user, auth.activeTenantId);
  const canAccess = activeMembership?.role !== 'LOCATION_USER';
  const superAdmin = auth.user?.globalRole === 'SUPER_ADMIN';

  const [status, setStatus] = useState('');
  const [locationId, setLocationId] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const locationsQuery = useLocations({
    page: 1,
    pageSize: 200,
  });
  const runsQuery = useRuns({
    page,
    pageSize,
    status: status || undefined,
    locationId: locationId || undefined,
    enabled: canAccess,
  });
  const runDetail = useRunDetail(canAccess ? selectedRunId : null);
  const retryRun = useRetryRun();

  const steps = useMemo(() => {
    if (!runDetail.data?.steps) {
      return [];
    }
    return runDetail.data.steps;
  }, [runDetail.data]);

  if (!canAccess) {
    return (
      <section className="card">
        <h2>Activity</h2>
        <p className="card-sub">
          Activity history is available to agency administrators. Ask your agency contact if you need access.
        </p>
      </section>
    );
  }

  const runRows = (runsQuery.data?.rows || []) as any[];

  return (
    <>
      <PageHeader
        title="Activity"
        description="Every post attempt, step by step. Select a run to see exactly what happened."
        actions={
          <>
            <select
              value={locationId}
              onChange={(event) => {
                setLocationId(event.target.value);
                setPage(1);
              }}
              style={{ width: 'auto' }}
            >
              <option value="">All locations</option>
              {(locationsQuery.data?.rows || []).map((location: any) => (
                <option key={location.id} value={location.id}>
                  {location.businessName}
                </option>
              ))}
            </select>
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
              style={{ width: 'auto' }}
            >
              <option value="">All statuses</option>
              <option value="QUEUED">Queued</option>
              <option value="RUNNING">Running</option>
              <option value="COMPLETED">Completed</option>
              <option value="PARTIAL_SUCCESS">Partly done</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </>
        }
      />

      <div className="split-layout split-layout-runs">
        <section className="card">
          {runsQuery.isLoading ? (
            <SkeletonList rows={6} height={74} />
          ) : runRows.length === 0 ? (
            <EmptyState
              icon={<Icon.Activity size={20} />}
              title="No activity yet"
              description="Posts will appear here as soon as a schedule fires or you publish one manually from Locations."
            />
          ) : (
            <div className="grid" style={{ gap: 8 }}>
              {runRows.map((run: any) => (
                <button
                  key={run.id}
                  className={`run-item ${selectedRunId === run.id ? 'selected' : ''}`}
                  onClick={() => setSelectedRunId(run.id)}
                >
                  <div className="inline" style={{ justifyContent: 'space-between' }}>
                    <strong>{run.location?.businessName || 'Location'}</strong>
                    <StatusBadge status={run.status} />
                  </div>
                  <span className="muted">
                    {TRIGGER_LABELS[run.triggerType] || run.triggerType} · {formatDateTimeEst(run.createdAt)}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="inline" style={{ justifyContent: 'space-between', marginTop: 14 }}>
            <span className="muted">
              {runRows.length} of {runsQuery.data?.total || 0} runs
            </span>
            <div className="inline">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </Button>
              <span className="muted">Page {page}</span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page * pageSize >= (runsQuery.data?.total || 0)}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </section>

        <section className="card">
          {!selectedRunId ? (
            <EmptyState
              icon={<Icon.Activity size={20} />}
              title="Select a run"
              description="Choose a run on the left to see its step-by-step timeline."
            />
          ) : (
            <>
              <div className="inline" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
                <h3>Run timeline</h3>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Icon.Refresh />}
                  onClick={() =>
                    retryRun.mutate(selectedRunId, {
                      onSuccess: () =>
                        toast({ title: 'Retry queued', description: 'The run will start again from the beginning.' }),
                      onError: (error) =>
                        toast({ variant: 'error', title: 'Could not retry', description: (error as Error).message }),
                    })
                  }
                  loading={retryRun.isPending}
                  loadingText="Queueing…"
                >
                  Try again
                </Button>
              </div>

              {runDetail.isLoading && <SkeletonList rows={5} height={48} />}

              {runDetail.data && (
                <>
                  <div className="inline" style={{ marginBottom: 16 }}>
                    <StatusBadge status={runDetail.data.status} />
                    <span className="muted mono">ID {runDetail.data.id.slice(0, 8)}</span>
                    {superAdmin && (
                      <span className="muted mono">${Number(runDetail.data.estimatedCostUsd || 0).toFixed(4)}</span>
                    )}
                  </div>

                  <div className="timeline">
                    {steps.map((step: any) => {
                      const meta = STEP_LABELS[step.name] || { label: step.name, explain: '' };
                      const toneClass =
                        step.status === 'COMPLETED'
                          ? 'ok'
                          : step.status === 'FAILED'
                            ? 'fail'
                            : step.status === 'SKIPPED'
                              ? 'skip'
                              : step.status === 'RUNNING' || step.status === 'RETRYING'
                                ? 'run'
                                : '';
                      const friendly = explainError(step.errorMessage);

                      return (
                        <div key={step.id} className={`timeline-step ${toneClass}`}>
                          <span className="timeline-dot">
                            {step.status === 'COMPLETED' ? (
                              <Icon.Check size={13} />
                            ) : step.status === 'FAILED' ? (
                              <Icon.X size={12} />
                            ) : step.status === 'RUNNING' || step.status === 'RETRYING' ? (
                              <span className="spinner" style={{ width: 11, height: 11 }} />
                            ) : (
                              <span style={{ fontSize: '0.68rem', fontWeight: 700 }}>{step.stepOrder}</span>
                            )}
                          </span>
                          <div className="timeline-body">
                            <span className="timeline-title">
                              {meta.label}
                              {step.status === 'SKIPPED' && <span className="badge neutral">Skipped</span>}
                            </span>
                            {meta.explain && <span className="timeline-meta">{meta.explain}</span>}
                            {(step.startedAt || step.endedAt) && (
                              <span className="timeline-meta mono">
                                {step.startedAt ? formatDateTimeEst(step.startedAt) : ''}
                                {step.endedAt ? ` → ${formatDateTimeEst(step.endedAt)}` : ''}
                              </span>
                            )}
                            {step.errorMessage && (
                              <div className="timeline-error">
                                {friendly ?? step.errorMessage}
                                {friendly && superAdmin && (
                                  <div className="mono" style={{ marginTop: 6, opacity: 0.75, fontSize: '0.74rem' }}>
                                    {step.errorMessage}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {superAdmin && (
                    <details className="help-details" style={{ marginTop: 14 }}>
                      <summary>
                        Technical snapshots
                        <Icon.Chevron className="chev" />
                      </summary>
                      <div className="help-details-body">
                        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                          <div>
                            <h4>Input</h4>
                            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.72rem', fontFamily: 'var(--font-mono)' }}>
                              {JSON.stringify(runDetail.data.inputSnapshot, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <h4>Output</h4>
                            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.72rem', fontFamily: 'var(--font-mono)' }}>
                              {JSON.stringify(runDetail.data.outputSnapshot, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </details>
                  )}
                </>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}
